import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, NavigationError, NavigationExtras, Params, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, publishReplay, refCount, startWith, tap } from 'rxjs/operators';

import * as _ from 'lodash';
import { detect } from 'detect-browser';
import { RouteHelper } from 'src/app/helpers';
import { HttpParams } from '@angular/common/http';
import { AuthService } from 'src/app/services/auth.service';

export const LOGIN_PATH = '/login';
export const ADMIN_LOGIN_PATH = '/admin/login';

@Injectable()
export class RouteService {
  public readonly helper = RouteHelper;
  public readonly primaryRoute$: Observable<ActivatedRoute>;

  private _primaryRoute!: ActivatedRoute;

  get primaryRoute(): ActivatedRoute {
    return this._primaryRoute;
  }

  private _redirectAfterLogin: string = localStorage.getItem('redirectAfterLogin') || '';

  constructor(public readonly router: Router, private activatedRoute: ActivatedRoute, private auth: AuthService) {
    this.primaryRoute$ = this.observePrimaryRoute();
    this.primaryRoute$.subscribe(route => (this._primaryRoute = route));
  }

  // #region Observables

  private observePrimaryRoute(): Observable<ActivatedRoute> {
    return this.router.events.pipe(
      startWith(() => null),
      tap(event => {
        if (event instanceof NavigationError) {
          console.warn('navigation error:', event.error?.message);
          this.goToLandingPage();
        }
        else if (event instanceof NavigationEnd) {
          console.log('navigated to', event.url);
        }
      }),
      filter(event => event === null || event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map(route => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter(route => route.outlet === 'primary'),
      publishReplay(1),
      refCount()
    );
  }

  // #endregion

  getCurrentUrl() {
    const url = this.getRelativeUrl(['.'], null, this._primaryRoute, { queryParamsHandling: 'merge' });
    return url;
  }

  getRelativeUrl(commands: any[], params?: any, route?: ActivatedRoute, options?: NavigationExtras) {
    route = route || this._primaryRoute || this.activatedRoute;
    const url = this.router.createUrlTree(commands, { ...options, relativeTo: route, queryParams: params });
    const relativeUrl = this.router.serializeUrl(url);
    return relativeUrl;
  }

  getResolvedUrl(route: ActivatedRouteSnapshot): string {
    const path = route.pathFromRoot
      .map(v => v.url.map(segment => segment.toString()).join('/'))
      .join('/');
    const queryParams = this.serializeQueryString(route.queryParams);
    const url = path && queryParams ? path + '?' + queryParams : path;
    return url;
  }

  getConfiguredUrl(route: ActivatedRouteSnapshot): string {
    return '/' + route.pathFromRoot
      .filter(v => v.routeConfig)
      .map(v => v.routeConfig!.path)
      .join('/');
  }

  serializeQueryString(params: Params): string {
    const httpParams = new HttpParams({
      fromObject: _.omitBy(params, _.isUndefined)
    });
    const queryParams = httpParams.toString();
    return queryParams; // foo=a&bar=42
  }

  rememberRedirectAfterLogin(url: string|null) {
    if (url) {
      this._redirectAfterLogin = url;
      localStorage.setItem('redirectAfterLogin', url);
    }
    else {
      this._redirectAfterLogin = '';
      localStorage.removeItem('redirectAfterLogin');
    }
  }

  redirectAfterLogin() {
    if (this._redirectAfterLogin) {
      const url = this._redirectAfterLogin;

      const isAdmin = this.auth.getAdminStatus();

      if (isAdmin && url.startsWith('/admin') === false) {
        this.goToLandingPage();
        return;
      }

      if (!isAdmin && url.startsWith('/admin') === true) {
        this.goToLandingPage();
        return;
      }

      this.router.navigateByUrl(url);

      this.rememberRedirectAfterLogin(null);
    }
    else {
      this.goToLandingPage();
    }
  }

  goToLandingPage() {
    if (!this.auth.authToken) {
      this.router.navigate(['/login']);
    }
    else if (this.auth.isAdmin || this.auth.isSuperAdmin) {
      this.router.navigate(['/admin/home']);
    }
    else {
      this.router.navigate(['/courses']);
    }
  }

  async forceNavigate(commands: any[], extras?: NavigationExtras) {
    const uri = this.router.createUrlTree(commands, extras).toString();
    const url = decodeURI(uri);
    await this.router.navigateByUrl('/blank', { skipLocationChange: true });
    await this.router.navigateByUrl(url, extras);
    setTimeout(() => this.scrollToTop());
  }

  scrollToTop() {
    const browser = detect();
    const top = { left: 0, top: 0 };
    if (browser?.name === 'firefox') {
      // scroll instantly in firefox due to weird bug ignoring scroll, for example when you hit Next on step 2 of user registration form
      window.scrollTo({ ...top, behavior: 'instant' as any });
    }
    else {
      window.scrollTo(top);
    }
  }
}
